import Component from "@ember/component";
import { action } from "@ember/object";
import { equal, or, readOnly } from "@ember/object/computed";
import { isEmpty } from "@ember/utils";
import ItsATrap from "@discourse/itsatrap";
import {
  BUMP_TYPE,
  CLOSE_AFTER_LAST_POST_STATUS_TYPE,
  CLOSE_STATUS_TYPE,
  DELETE_REPLIES_TYPE,
  DELETE_STATUS_TYPE,
  OPEN_STATUS_TYPE,
  PUBLISH_TO_CATEGORY_STATUS_TYPE,
} from "discourse/components/modal/edit-topic-timer";
import KeyboardShortcuts from "discourse/lib/keyboard-shortcuts";
import {
  TIME_SHORTCUT_TYPES,
  timeShortcuts,
} from "discourse/lib/time-shortcut";
import discourseComputed from "discourse-common/utils/decorators";
import I18n from "discourse-i18n";
import { FORMAT } from "select-kit/components/future-date-input-selector";

export default Component.extend({
  statusType: readOnly("topicTimer.status_type"),
  autoOpen: equal("statusType", OPEN_STATUS_TYPE),
  autoClose: equal("statusType", CLOSE_STATUS_TYPE),
  autoCloseAfterLastPost: equal(
    "statusType",
    CLOSE_AFTER_LAST_POST_STATUS_TYPE
  ),
  autoDelete: equal("statusType", DELETE_STATUS_TYPE),
  autoBump: equal("statusType", BUMP_TYPE),
  publishToCategory: equal("statusType", PUBLISH_TO_CATEGORY_STATUS_TYPE),
  autoDeleteReplies: equal("statusType", DELETE_REPLIES_TYPE),
  showTimeOnly: or("autoOpen", "autoDelete", "autoBump"),
  showFutureDateInput: or("showTimeOnly", "publishToCategory", "autoClose"),
  useDuration: or(
    "isBasedOnLastPost",
    "autoDeleteReplies",
    "autoCloseAfterLastPost"
  ),
  duration: null,
  _itsatrap: null,

  init() {
    this._super(...arguments);

    KeyboardShortcuts.pause();
    this.set("_itsatrap", new ItsATrap());

    this.set("duration", this.initialDuration);
  },

  get initialDuration() {
    if (!this.useDuration || !this.topicTimer.duration_minutes) {
      return null;
    } else if (this.durationType === "days") {
      return this.topicTimer.duration_minutes / 60 / 24;
    } else {
      return this.topicTimer.duration_minutes / 60;
    }
  },

  willDestroyElement() {
    this._super(...arguments);

    this._itsatrap.destroy();
    this.set("_itsatrap", null);
    KeyboardShortcuts.unpause();
  },

  @discourseComputed("autoDeleteReplies")
  durationType(autoDeleteReplies) {
    return autoDeleteReplies ? "days" : "hours";
  },

  @discourseComputed("topic.visible")
  excludeCategoryId(visible) {
    if (visible) {
      return this.get("topic.category_id");
    }
  },

  @discourseComputed()
  timeOptions() {
    const timezone = this.currentUser.user_option.timezone;
    const shortcuts = timeShortcuts(timezone);

    return [
      shortcuts.laterToday(),
      shortcuts.tomorrow(),
      shortcuts.laterThisWeek(),
      shortcuts.thisWeekend(),
      shortcuts.monday(),
      shortcuts.twoWeeks(),
      shortcuts.nextMonth(),
      shortcuts.sixMonths(),
    ];
  },

  @discourseComputed
  hiddenTimeShortcutOptions() {
    return [
      TIME_SHORTCUT_TYPES.NONE,
      TIME_SHORTCUT_TYPES.LATER_TODAY,
      TIME_SHORTCUT_TYPES.LATER_THIS_WEEK,
    ];
  },

  isCustom: equal("timerType", "custom"),
  isBasedOnLastPost: equal("statusType", "close_after_last_post"),

  @discourseComputed(
    "topicTimer.updateTime",
    "topicTimer.duration_minutes",
    "useDuration"
  )
  executeAt(updateTime, duration, useDuration) {
    if (useDuration) {
      return moment().add(parseFloat(duration), "minutes").format(FORMAT);
    } else {
      return updateTime;
    }
  },

  @discourseComputed(
    "isBasedOnLastPost",
    "topicTimer.duration_minutes",
    "topic.last_posted_at"
  )
  willCloseImmediately(isBasedOnLastPost, duration, lastPostedAt) {
    if (isBasedOnLastPost && duration) {
      let closeDate = moment(lastPostedAt);
      closeDate = closeDate.add(duration, "minutes");
      return closeDate < moment();
    }
  },

  @discourseComputed("isBasedOnLastPost", "topic.last_posted_at")
  willCloseI18n(isBasedOnLastPost, lastPostedAt) {
    if (isBasedOnLastPost) {
      const diff = Math.round(
        (new Date() - new Date(lastPostedAt)) / (1000 * 60 * 60)
      );
      return I18n.t("topic.auto_close_momentarily", { count: diff });
    }
  },

  @discourseComputed("durationType")
  durationLabel(durationType) {
    return I18n.t(`topic.topic_status_update.num_of_${durationType}`);
  },

  @discourseComputed(
    "statusType",
    "isCustom",
    "topicTimer.updateTime",
    "willCloseImmediately",
    "topicTimer.category_id",
    "useDuration",
    "topicTimer.duration_minutes"
  )
  showTopicTimerInfo(
    statusType,
    isCustom,
    updateTime,
    willCloseImmediately,
    categoryId,
    useDuration,
    duration
  ) {
    if (!statusType || willCloseImmediately) {
      return false;
    }

    if (statusType === PUBLISH_TO_CATEGORY_STATUS_TYPE && isEmpty(categoryId)) {
      return false;
    }

    if (isCustom && updateTime) {
      if (moment(updateTime) < moment()) {
        return false;
      }
    } else if (useDuration) {
      return duration;
    }

    return updateTime;
  },

  @action
  onTimeSelected(type, time) {
    this.set("timerType", type);
    this.onChangeInput(type, time);
  },

  @action
  durationChanged(newDurationMins) {
    this.set("topicTimer.duration_minutes", newDurationMins);
  },
});
